import React          from 'react';
import styled         from 'styled-components';
import {
  Form,
  Field,
  Formik,
  FieldArray,
  FormikProps,
}                     from 'formik';
import { Grid }       from '@material-ui/core';
import {
  IPhoto,
  TRotationDegree,
}                     from '@typings/auction';
import { EPhotoType } from '@enums/auction';
import { PhotoItem }  from '../components/PhotoItem';
import {
  PHOTO_NAME,
  PHOTO_PLACEHOLDER,
}                     from '../utils/placeholderImages';

export const photoTypesOrder = [
  EPhotoType.PassengerSideFrontQuarter,
  EPhotoType.FrontDistant,
  EPhotoType.DriverSideFrontQuarter,
  EPhotoType.DriverSide,
  EPhotoType.DriverSideRearQuarter,
  EPhotoType.RearDistant,
  EPhotoType.PassengerSideRearQuarter,
  EPhotoType.PassengerSide,
  EPhotoType.TireCloseup,
  EPhotoType.Interior,
  EPhotoType.Odometer,
  EPhotoType.EngineCloseup,
];

const Wrapper = styled.div`
  background-color: ${({ theme: { backgroundColor } }): string => backgroundColor.white};
`;

const DescriptionWrapper = styled.div`
  display         : flex;
  justify-content : space-around;
  width           : 100%;
  padding-top     : 40px;

  @media (min-width: 768px) {
    padding-top: 70px;
  }
`;

const DescriptionContainer = styled.div`
  max-width : 750px;
  width     : 100%;
  padding   : 0 23px;

  h1 {
    font-weight : 400;
    text-align  : center;
  }
`;

const ImageWrapper = styled.div`
  display         : flex;
  justify-content : ${({ justify }: any): string => (justify || 'space-around')};
  flex-wrap       : wrap;
  width           : 100%;
  margin          : 60px auto 0 auto;
  padding         : 0 25px;

  @media (min-width: 768px) {
    width: 85%;
  }
` as any;

const BorderLine = styled.div`
  height           : 1px;
  width            : 90%;
  background-color : #B5B5B5;
  margin           : 0 auto 50px auto;
`;

interface IProps {
  photos            : IPhoto[];
  isButtonsDisabled : boolean;
  isRemoveDisabled  : boolean;
  handleUploadPhoto : (
    photoType : EPhotoType,
    image     : File,
  ) => Promise<void>;
  handleRotatePhoto : (
    photoId : string,
    degree  : TRotationDegree,
  ) => Promise<void>;
  handleRemovePhoto : (photoId: string) => Promise<void>;
}

export const AuctionPhotosPage = ({
  photos,
  handleUploadPhoto,
  handleRotatePhoto,
  handleRemovePhoto,
  isButtonsDisabled,
  isRemoveDisabled,
}: IProps): JSX.Element => (
  <Wrapper>
    <Formik
      enableReinitialize
      onSubmit      = {(): any => null}
      initialValues = {photos}
    >
      {({ values }: FormikProps<any>): JSX.Element => (
        <Form>
          <DescriptionWrapper>
            <DescriptionContainer>
              <Grid container spacing={0}>
                <Grid item xs={12}>
                  <h1>Photo Gallery</h1>
                </Grid>
              </Grid>
            </DescriptionContainer>
          </DescriptionWrapper>

          <FieldArray
            name   = "photos"
            render = {
              (): JSX.Element => (
                <ImageWrapper>
                  {photoTypesOrder.map(
                    (photoType): JSX.Element => (
                      <Field
                        id               = {`${photoType}`}
                        key              = {`${photoType}`}
                        name             = {`${photoType}`}
                        title            = {PHOTO_NAME[photoType]}
                        photo            = {
                          (values ?? []).find(
                            (photo: IPhoto): boolean =>
                              photo.photoType === photoType,
                          )
                        }
                        photoType         = {photoType}
                        component         = {PhotoItem}
                        placeholderImage  = {PHOTO_PLACEHOLDER[photoType]}
                        handleUploadPhoto = {handleUploadPhoto}
                        handleRotatePhoto = {handleRotatePhoto}
                        handleRemovePhoto = {handleRemovePhoto}
                        isButtonsDisabled = {isButtonsDisabled}
                        isRemoveDisabled  = {isRemoveDisabled}
                      />
                    ),
                  )}
                </ImageWrapper>
              )
            }
          />

          <BorderLine />

          <ImageWrapper>
            {(values ?? [])
              .filter(
                (photo: IPhoto): boolean =>
                  photo?.photoType === EPhotoType.Other,
              )
              .map(
                (photo: IPhoto): JSX.Element => (
                  <Field
                    id                = {photo.id}
                    key               = {photo.id}
                    name              = {photo.id}
                    title             = {PHOTO_NAME[EPhotoType.Other]}
                    photo             = {photo}
                    photoType         = {EPhotoType.Other}
                    component         = {PhotoItem}
                    placeholderImage  = {PHOTO_PLACEHOLDER[EPhotoType.Other]}
                    handleRotatePhoto = {handleRotatePhoto}
                    handleRemovePhoto = {handleRemovePhoto}
                    isButtonsDisabled = {isButtonsDisabled}
                  />
                ),
              )}
          </ImageWrapper>
        </Form>
      )}
    </Formik>
  </Wrapper>
);
